import React, { useState } from 'react';
import {
    Box,
    Button,
    Typography,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    Divider,
    Grid
} from '@mui/material';
import {
    Business as BusinessIcon,
    Factory as FactoryIcon,
    Security as SecurityIcon
} from '@mui/icons-material';

import StandaloneRolePermissionManager from '@/components/StandAlonePermissionManager';
import { usePutApiClientsId, usePutApiPlants } from '@/api/gen/api';
import { useDataProvider } from 'react-admin';

interface RolePermission {
    role: string;  
    permissionLevel: 'READ' | 'WRITE' | 'ADMIN';
}

interface CreatedRole {
    id: string;
    name: string;
}

interface ClientData {
    id: number;
    clientName: string;
    clientPhoneContact: string;
    clientEmail: string;
}

interface PlantData {
    id: number;
    plantName: string;
    clientPhoneContact: string;
    clientEmail: string;
    clientId: number;
}

interface StepRolesAndPermissionsProps {
    onNext: (data: { 
        rolePermissions: RolePermission[];
        createdRoles: CreatedRole[];
    }) => void;
    clientData: ClientData;
    plantData: PlantData;
}

// -------------------------- COMPONENTE PRINCIPAL --------------------------
const StepRolRegisterClient: React.FC<StepRolesAndPermissionsProps> = ({
    onNext,
    clientData,
    plantData
}) => {
    // -------------------------- ESTADOS --------------------------
    const [rolePermissions, setRolePermissions] = useState<RolePermission[]>([
        { role: 'ADMIN', permissionLevel: 'ADMIN' }
    ]);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // -------------------------- HOOKS DE MUTACIÓN --------------------------
    const { mutateAsync: updateClient } = usePutApiClientsId();
    const { mutateAsync: updatePlant } = usePutApiPlants();
    const dataProvider = useDataProvider();

    // -------------------------- MANEJADORES --------------------------
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {

            // Validaciones
            if (rolePermissions.length === 0) {
                setError('Debe configurar al menos un rol');
                setIsLoading(false);
                return;
            }

            const hasEmptyRoles = rolePermissions.some(perm => !perm.role.trim());
            const hasInvalidRoleNames = rolePermissions.some(perm => 
                perm.role.trim().length < 2
            );
            
            if (hasEmptyRoles) {
                setError('Todos los roles deben tener un nombre');
                setIsLoading(false);
                return;
            }

            if (hasInvalidRoleNames) {
                setError('Los nombres de roles deben tener al menos 2 caracteres');
                setIsLoading(false);
                return;
            }

            // 1. CREAR NUEVOS ROLES
            
            const createdRoles: CreatedRole[] = [];
            
            for (const perm of rolePermissions) {
                try {
                    const newRole = await dataProvider.create('roles', {
                        data: {
                            name: perm.role.toUpperCase()
                        }
                    });

                    createdRoles.push({
                        id: newRole.data.id,
                        name: perm.role.toUpperCase()
                    });
                    
                } catch (error) {
                    console.error(`❌ Error creando rol ${perm.role}:`, error);
                    throw new Error(`No se pudo crear el rol "${perm.role}": ${error.message}`);
                }
            }

            

            // 2. ASIGNA ROLES AL CLIENTE Y PLANTA USANDO LOS NOMBRES
            const rolePermissionsForClientAndPlant = rolePermissions.map(perm => ({
                role: perm.role.toUpperCase(),  
                permissionLevel: perm.permissionLevel
            }));

            // Preparar datos para actualizar el cliente
            const clientUpdateData = {
                id: clientData.id,
                clientName: clientData.clientName,
                clientPhoneContact: clientData.clientPhoneContact,
                clientEmail: clientData.clientEmail,
                rolePermissions: rolePermissionsForClientAndPlant  // array
            };

            // Preparar datos para actualizar la planta
            const plantUpdateData = {
                id: plantData.id,
                plantName: plantData.plantName,
                clientPhoneContact: plantData.clientPhoneContact,
                clientEmail: plantData.clientEmail,
                clientId: plantData.clientId,
                rolePermissions: rolePermissionsForClientAndPlant  // array
            };


            // 3. PEQUEÑO DELAY PARA ASEGURAR QUE LOS ROLES ESTÁN DISPONIBLES
            await new Promise(resolve => setTimeout(resolve, 1000));

            // 4. ACTUALIZAR CLIENTE Y PLANTA
            const clientResult = await updateClient({ 
                id: clientData.id, 
                data: clientUpdateData 
            });

            if (clientResult.success === false) {
                const errorMsg = clientResult.errors?.[0]?.description || 'Error desconocido al actualizar cliente'
                throw new Error(`Cliente: ${errorMsg}`);
            }

            const plantResult = await updatePlant({ 
                data: plantUpdateData,
                params: { id: plantData.id }
            });

            if (plantResult.success === false) {
                const errorMsg = plantResult.errors?.[0]?.description || 'Error desconocido al actualizar planta';
                console.error('❌ Error del servidor en planta:', errorMsg);
                throw new Error(`Planta: ${errorMsg}`);
            }

            onNext({
                rolePermissions,
                createdRoles
            });

        } catch (error) {
            console.error('❌ Error en creación de roles:', error);
            setError(error.message || 'Error al crear y asignar los roles. Por favor, intente nuevamente.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRolesChange = (newRolePermissions: RolePermission[]) => {
        setRolePermissions(newRolePermissions);
    };

    // -------------------------- RENDERIZADO --------------------------

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mt: 1, mb: 3 }}>
                Se crearán nuevos roles y se asignarán automáticamente al cliente y la planta. 
                Escriba los nombres de los roles que desea crear.
            </Alert>

            {/* Título */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <SecurityIcon color='primary' sx={{ fontSize: 32 }} />
                <Typography variant='h4' component='h1' fontWeight='bold'>
                    Crear y Asignar Roles
                </Typography>
            </Box>

            {/* Información de Resumen */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Información del Cliente */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ 
                        height: 'fit-content', 
                        boxShadow: 3, 
                        borderRadius: 2,
                        backgroundColor: 'primary.50',
                        border: '1px solid',
                        borderColor: 'primary.200'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <BusinessIcon color='primary' />
                                <Typography variant='h6' fontWeight='bold' color='primary.main'>
                                    Cliente
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant="body1">
                                    <strong>Nombre:</strong> {clientData.clientName}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>ID:</strong> {clientData.id}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>Email:</strong> {clientData.clientEmail}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>Teléfono:</strong> {clientData.clientPhoneContact}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Información de la Planta */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ 
                        height: 'fit-content', 
                        boxShadow: 3, 
                        borderRadius: 2,
                        backgroundColor: 'info.50',
                        border: '1px solid',
                        borderColor: 'info.200'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <FactoryIcon color='info' />
                                <Typography variant='h6' fontWeight='bold' color='info.main'>
                                    Planta
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant="body1">
                                    <strong>Nombre:</strong> {plantData.plantName}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>ID:</strong> {plantData.id}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>Email:</strong> {plantData.clientEmail}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>Teléfono:</strong> {plantData.clientPhoneContact}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Sección Única de Roles y Permisos */}
            <StandaloneRolePermissionManager
                title="Crear Nuevos Roles"
                subtitle="Los roles se crearán automáticamente y se asignarán al cliente y planta"
                helperText="Escriba los nombres de los nuevos roles que desea crear (ej: Administrador, Supervisor, Operador)"
                value={rolePermissions}
                onChange={handleRolesChange}
                disabled={isLoading}
            />

            {/* Mostrar error si existe */}
            {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            )}

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    * Se crearán nuevos roles con los nombres especificados
                </Typography>
            </Box>

            {/* Botón de envío */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={20} /> : null}
                    sx={{ borderRadius: 2 }}
                >
                    {isLoading ? 'Creando Roles...' : 'Crear Roles y Continuar'}
                </Button>
            </Box>
        </Box>
    );
};

export default StepRolRegisterClient;